import { useState, useEffect, useRef, useCallback } from 'react';
import {
  suspendProfile,
  activateProfile,
  removeProfile,
  getRemovedProfiles,
} from '../../services/businessService';
import type { BusinessProfile } from '../../types/business';
import { isProtectedBusinessOwner } from '../../types/profile';
import { ConfirmProfileActionModal } from '../profiles/ConfirmProfileActionModal';

type ProfileAction = 'suspend' | 'activate' | 'remove';

interface ManageClientAdminsModalProps {
  businessId: string;
  profiles: BusinessProfile[];
  onClose: () => void;
  onSuccess: () => void;
  onAddAdmin: () => void;
}

function profileRoleLabel(roles: string[]): string {
  if (roles.includes('client_owner')) return 'Owner';
  if (roles.includes('client_admin')) return 'Admin';
  return 'User';
}

function statusBadgeClass(status?: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'suspended':
      return 'bg-orange-100 text-orange-800';
    case 'deleted':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function ProfileRowMenu({
  profile,
  isOwner,
  onAction,
}: {
  profile: BusinessProfile;
  isOwner: boolean;
  onAction: (action: ProfileAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const status = profile.status || 'active';
  const canSuspend = !isOwner && (status === 'active' || status === 'pending');
  const canActivate = status === 'suspended';
  const canRemove = !isOwner && status !== 'deleted';

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!canSuspend && !canActivate && !canRemove) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
          {canSuspend && (
            <button
              type="button"
              onClick={() => { setOpen(false); onAction('suspend'); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Suspend
            </button>
          )}
          {canActivate && (
            <button
              type="button"
              onClick={() => { setOpen(false); onAction('activate'); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Activate
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={() => { setOpen(false); onAction('remove'); }}
              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-gray-50"
            >
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ManageClientAdminsModal({
  businessId,
  profiles,
  onClose,
  onSuccess,
  onAddAdmin,
}: ManageClientAdminsModalProps) {
  const [tab, setTab] = useState<'active' | 'removed'>('active');
  const [removedProfiles, setRemovedProfiles] = useState<BusinessProfile[]>([]);
  const [removedLoading, setRemovedLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ action: ProfileAction; profile: BusinessProfile } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRemoved = useCallback(async () => {
    setRemovedLoading(true);
    try {
      const removed = await getRemovedProfiles(businessId);
      setRemovedProfiles(removed);
    } catch {
      setRemovedProfiles([]);
    } finally {
      setRemovedLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    if (tab === 'removed') fetchRemoved();
  }, [tab, fetchRemoved]);

  const activeProfiles = profiles.filter(
    (p) => p.roles?.includes('client_owner') || p.roles?.includes('client_admin'),
  );

  const handleConfirm = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      const { action, profile } = confirmAction;
      if (action === 'suspend') await suspendProfile(businessId, profile.id);
      if (action === 'activate') await activateProfile(businessId, profile.id);
      if (action === 'remove') await removeProfile(businessId, profile.id);
      setConfirmAction(null);
      onSuccess();
      if (tab === 'removed') fetchRemoved();
    } catch {
      // error handled by apiService
    } finally {
      setActionLoading(false);
    }
  };

  const renderProfileRow = (profile: BusinessProfile, showActivateOnly = false) => {
    const isOwner = isProtectedBusinessOwner(profile.roles);
    return (
      <div key={profile.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-b-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{profile.displayName}</span>
            <span className="text-xs text-gray-500">{profileRoleLabel(profile.roles || [])}</span>
            {profile.status && (
              <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${statusBadgeClass(profile.status)}`}>
                {profile.status.charAt(0).toUpperCase() + profile.status.slice(1)}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 truncate">{profile.email}</p>
        </div>
        {showActivateOnly ? (
          <button
            type="button"
            onClick={() => setConfirmAction({ action: 'activate', profile })}
            className="shrink-0 px-3 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Activate
          </button>
        ) : (
          <ProfileRowMenu
            profile={profile}
            isOwner={isOwner}
            onAction={(action) => setConfirmAction({ action, profile })}
          />
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Manage Client Admins</h2>
            <button
              type="button"
              onClick={() => { onClose(); onAddAdmin(); }}
              className="px-3 py-1 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Add Admin
            </button>
          </div>

          <div className="flex gap-1 mb-4 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setTab('active')}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'active'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Active ({activeProfiles.length})
            </button>
            <button
              type="button"
              onClick={() => setTab('removed')}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === 'removed'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Removed
            </button>
          </div>

          {tab === 'active' && (
            activeProfiles.length > 0
              ? activeProfiles.map((profile) => renderProfileRow(profile))
              : <p className="text-sm text-gray-500">No client admins found.</p>
          )}

          {tab === 'removed' && (
            removedLoading
              ? <p className="text-sm text-gray-400">Loading...</p>
              : removedProfiles.length > 0
                ? removedProfiles.map((profile) => renderProfileRow(profile, true))
                : <p className="text-sm text-gray-500">No removed profiles.</p>
          )}
        </div>

        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {confirmAction && (
        <ConfirmProfileActionModal
          action={confirmAction.action}
          displayName={confirmAction.profile.displayName}
          contextLabel="client"
          loading={actionLoading}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
